import {
  DEFAULT_HOST_LOCALE,
  type HostLocale,
  hostLocaleOf,
} from "@vidimo/shared";

/**
 * One signal about the language a host reads: a tag, `null` when it is known
 * to be absent, or `undefined` while it is not known yet (a staff row still
 * loading, a session still resolving or out of reach).
 */
export type LocaleSignal = string | null | undefined;

/** Which step decided the interface language. */
export type InterfaceLocaleSource =
  | "explicit"
  | "signup"
  | "link"
  | "mirror"
  | "device"
  | "default";

export interface ResolvedInterfaceLocale {
  /**
   * What "Automatic" would resolve to: the same walk with no explicit pick.
   * While a signal it needs is still unknown, the best reading of the signals
   * that are known.
   */
  automaticLocale: HostLocale;
  locale: HostLocale;
  source: InterfaceLocaleSource;
}

export type SignedInLocaleResolution =
  | ({ pending: false } & ResolvedInterfaceLocale)
  | {
      pending: true;
      /**
       * The walk with every unknown signal taken as absent: what the screen
       * would show if they never arrived. A reading, not a settled answer.
       */
      provisional: ResolvedInterfaceLocale;
    };

export interface SignedInSignals {
  /** The languages the device lists, most preferred first. */
  device: readonly string[];
  /** The host's own pick (`staff.displayLanguage`); null means Automatic. */
  explicit: LocaleSignal;
  /** The language the account was opened in, as the session reports it. */
  signup: LocaleSignal;
}

export interface SignedOutSignals {
  device: readonly string[];
  /**
   * The language a connect page's link carried: the one the host reads the
   * app in, on an origin that cannot read the app's storage.
   */
  hostLink: string | null;
  /** The language a signup or login link carried on this page load. */
  link: string | null;
  /** The last language a signed-in host settled on, on this device. */
  mirror: string | null;
}

interface LocaleStep {
  signal: LocaleSignal;
  source: InterfaceLocaleSource;
}

interface Landing {
  locale: HostLocale;
  source: InterfaceLocaleSource;
}

/** A walk that reached a step still unknown before any step decided. */
const PENDING = "pending";

const DEFAULT_LANDING: Landing = {
  locale: DEFAULT_HOST_LOCALE,
  source: "default",
};

/**
 * The first step that lands on a host locale. A step whose signal names
 * nothing, or names a language the app does not speak, falls through; none
 * stops the chain. A step still unknown before any step has decided makes the
 * whole walk pending, because the steps after it are weaker.
 */
function walk(steps: readonly LocaleStep[]): Landing | typeof PENDING {
  for (const { signal, source } of steps) {
    if (signal === undefined) {
      return PENDING;
    }
    const locale = hostLocaleOf(signal);
    if (locale) {
      return { locale, source };
    }
  }
  return DEFAULT_LANDING;
}

/** The walk as it reads with every unknown signal taken as absent. */
function bestReading(steps: readonly LocaleStep[]): Landing {
  const landing = walk(
    steps.map((step) => ({ ...step, signal: step.signal ?? null }))
  );
  return landing === PENDING ? DEFAULT_LANDING : landing;
}

function deviceSteps(device: readonly string[]): LocaleStep[] {
  return device.map((signal) => ({ signal, source: "device" }));
}

/**
 * The interface language for a signed-in host:
 *
 * 1. Their explicit pick.
 * 2. Their device's languages, in its order.
 * 3. The language their account was opened in, for a device whose languages
 *    the app does not ship.
 * 4. English.
 *
 * Pending while a step that could still decide is unknown, so the caller
 * keeps the language on screen rather than switch twice; an explicit pick, or
 * a device language the app ships, decides without waiting for the session.
 */
export function resolveSignedInLocale({
  device,
  explicit,
  signup,
}: SignedInSignals): SignedInLocaleResolution {
  const automaticSteps: LocaleStep[] = [
    ...deviceSteps(device),
    { signal: signup, source: "signup" },
  ];
  const steps: LocaleStep[] = [
    { signal: explicit, source: "explicit" },
    ...automaticSteps,
  ];
  const landing = walk(steps);
  const automaticLocale = bestReading(automaticSteps).locale;
  if (landing === PENDING) {
    return {
      pending: true,
      provisional: { automaticLocale, ...bestReading(steps) },
    };
  }
  return { automaticLocale, pending: false, ...landing };
}

/**
 * The interface language before sign-in, the first frame at boot included:
 *
 * 1. The language a connect page's link carried, the host's own.
 * 2. The language this device last settled on for a signed-in host, so the
 *    login screen stays in the language just used.
 * 3. The device's languages, in its order.
 * 4. The language a signup or login link carried, for a device whose
 *    languages the app does not ship.
 * 5. English.
 *
 * Every signal here is already known, so this never waits.
 */
export function resolveSignedOutLocale({
  device,
  hostLink,
  link,
  mirror,
}: SignedOutSignals): ResolvedInterfaceLocale {
  const { locale, source } = bestReading([
    { signal: hostLink, source: "link" },
    { signal: mirror, source: "mirror" },
    ...deviceSteps(device),
    { signal: link, source: "link" },
  ]);
  return { automaticLocale: locale, locale, source };
}
